import {} from '../schema.js';
import { formatNumber } from '../util/functions.js';
import {} from './fields.js';
import { formatReportDate, formatReportTime, fullNameOf } from './shared.js';
function strengthOf(movement) {
    const value = movement.batch?.tradeName?.strengthValue;
    if (value == null)
        return null;
    const unit = movement.batch?.tradeName?.strengthUnit?.ucumCode;
    return unit ? `${value} ${unit}` : String(value);
}
/** The drug-catalog item behind the batch's trade name (its substance / category source). */
function drugItemOf(movement) {
    return movement.batch?.tradeName?.prescribedName?.drugCatalogItem;
}
function categoryOf(movement, kind) {
    const categories = drugItemOf(movement)?.categories ?? [];
    const match = categories.find(entry => kind === 'category' ? entry.drugCategory && !entry.drugCategory.parentId : Boolean(entry.drugCategory?.parentId));
    return match?.drugCategory?.name ?? null;
}
/** Returns the raw, unformatted value for a single field of a stock-movement report row. */
function rawFieldValue(movement, fieldId) {
    const batch = movement.batch;
    const tradeName = batch?.tradeName;
    const sequence = movement.patientTherapySequence;
    const patient = sequence?.patient;
    const encounter = patient?.encounters?.[0];
    switch (fieldId) {
        case 'date':
            return movement.performedAt;
        case 'time':
            return movement.performedAt;
        case 'movementType':
            return movement.type;
        case 'quantity':
            return movement.quantity == null ? null : Number(movement.quantity);
        case 'quantityUnit':
            return movement.quantityUnit?.displayName ?? movement.quantityUnit?.ucumCode ?? null;
        case 'packages':
            return movement.packages == null ? null : Number(movement.packages);
        case 'source':
            return movement.source ?? null;
        case 'reason':
            return movement.reason ?? null;
        case 'note':
            return movement.note ?? null;
        case 'user':
            return fullNameOf(movement.created) || null;
        case 'tradeName':
            return tradeName?.name ?? batch?.consumable?.name ?? null;
        case 'activeSubstance':
            return drugItemOf(movement)?.name ?? null;
        case 'strength':
            return strengthOf(movement);
        case 'medicineForm':
            return null;
        case 'packSize':
            return tradeName?.unitsPerPackage ?? null;
        case 'productCode':
            return tradeName?.barcodeGtin ?? null;
        case 'manufacturer':
            return tradeName?.manufacturer?.name ?? null;
        case 'category':
            return categoryOf(movement, 'category');
        case 'subcategory':
            return categoryOf(movement, 'subcategory');
        case 'prescriptionTradeName':
            return null;
        case 'storageLocation':
            return movement.stockLocation?.name ?? batch?.stockLocation?.name ?? null;
        case 'batchNumber':
            return batch?.lotNumber ?? null;
        case 'expiry':
            return batch?.expiryDate ?? null;
        case 'supplier':
            return batch?.supplierCompany?.name ?? null;
        case 'receivedOn':
            return batch?.receivedAt ?? null;
        case 'prescriptionNumber':
            return sequence?.internalId ?? null;
        case 'productionNumber':
            // the production number is the prescription number, as on the label (CY-216)
            return sequence?.internalId ?? null;
        case 'pickingListNumber':
            return movement.order?.internalId ?? null;
        case 'patientName':
            return patient?.fullName ?? null;
        case 'hospitalId':
            return patient?.hospitalPatientId ?? null;
        case 'ward':
            return encounter?.ward?.name ?? null;
        case 'section':
            return encounter?.section?.name ?? null;
        case 'customer':
            return encounter?.customer?.name ?? null;
        case 'responsibleDoctor':
            return fullNameOf(patient?.responsibleDoctor) || null;
        case 'protocol':
            return sequence?.protocol?.name ?? null;
        case 'applicationDate':
            return sequence?.therapyDate ?? null;
        case 'restVialNumber':
            return movement.restVial?.restVialNumber != null ? String(movement.restVial.restVialNumber) : null;
        case 'deliveryNoteNumber':
            return movement.deliveryNoteNumber ?? null;
        case 'unitPrice':
            return null;
        case 'value':
            return null;
        case 'hospitalSystemOrderNumber':
            return null;
        default:
            return null;
    }
}
/** Fields whose value is a machine token rather than display text. */
function translateFieldValue(raw, fieldId, tr) {
    switch (fieldId) {
        case 'movementType':
            return tr.report(`movement_type_${raw}`);
        case 'source':
            return tr.report(`source_${raw}`);
        case 'reason':
            return tr.report(`discard_reason_${raw}`);
        default:
            return null;
    }
}
/** Formats a single field to the string shown in a cell line (dates DD.MM.YYYY, times HH:mm, ...). */
export function formatStockMovementField(movement, field, tr) {
    const raw = rawFieldValue(movement, field.id);
    if (raw === null || raw === undefined || raw === '')
        return null;
    const translated = translateFieldValue(raw, field.id, tr);
    if (translated !== null)
        return translated;
    switch (field.id) {
        case 'date':
        case 'expiry':
        case 'receivedOn':
        case 'applicationDate':
            return formatReportDate(raw);
        case 'time':
            return formatReportTime(raw);
        case 'quantity':
        case 'packages':
        case 'packSize':
            return formatNumber(Number(raw), true);
        default:
            return String(raw);
    }
}
/** One stacked line per cell for a stock-movement row. */
export function getStockMovementCellLines(movement, column, tr) {
    const lines = [];
    for (const field of column.fields) {
        const value = formatStockMovementField(movement, field, tr);
        if (value !== null)
            lines.push(value);
    }
    // the quantity cell shows "value unit" on one line; a movement recorded without its own unit counts in plain units
    if (column.fields[0]?.cellGroup === 'quantity') {
        const quantity = rawFieldValue(movement, 'quantity');
        // a transfer or a return records no amount; do not leave a bare unit in the cell
        if (quantity == null)
            return [];
        const unit = rawFieldValue(movement, 'quantityUnit') ?? tr.report('units');
        const line = [formatNumber(Number(quantity), true), unit].filter(Boolean).join(' ');
        return line ? [line] : [];
    }
    return lines.filter(Boolean);
}
//# sourceMappingURL=stock-movement-values.js.map